import anime from 'animejs'

/**
 * Mobile menu UI component.
 * See Figma designs: https://www.figma.com/file/YQ73BIauH99X8ksuCY1hcZ/Marketing---website-content-blueprint?type=design&node-id=639-2668&t=kQ83llMh84LyGzLD-4
 */
const MobileMenu = (function () {
  // DOM elements
  const headerEl = document.querySelector('.js-header')
  const navEl = headerEl.querySelector('.js-main-nav')
  const toggleBtnEl = headerEl.querySelector('.js-mob-menu-toggle-btn')
  const subMenuEls = headerEl.querySelectorAll('.js-sub-menu')
  const toggleSubMenuBtnEls = headerEl.querySelectorAll('.js-toggle-sub-menu')
  const overlayEl = headerEl.querySelector('.js-menu-bg-overlay')
  const tier1Links = headerEl.querySelectorAll('.js-tier-1-link')
  const tier2Links = headerEl.querySelectorAll('.js-tier-2-link')
  const linksOutsideFocusTrap = headerEl.querySelectorAll('.js-link-outside-menu')
  const homeLogoLink = headerEl.querySelector('.js-home-logo-link')
  const body = document.querySelector('body')
  const html = document.querySelector('html')

  // Menu state
  let menuState = {
    isOpen: false,
    activeSubMenuId: '',
    isAnimating: false,
  }

  // Expand/contract animation config. See https://animejs.com/documentation
  const menuAnimationOpts = {
    speed: 250,
    easing: 'cubicBezier(0.22, 1, 0.36, 1)',
  }

  // Fade in/out menu bg overlay config
  const overlayAnimationOpts = {
    speed: 200,
    easing: 'linear',
  }

  /**
   * - Set up event listeners to toggle main and sub menus.
   * - Prevent keyboard navigation to visibly hidden link elements
   * - Set up event listener to force close menu on link click (due to menu state being retained between transitions)
   */
  const initMobileMenu = () => {
    toggleBtnEl.addEventListener('click', handleToggleBtnClick)
    toggleSubMenuBtnEls.forEach((toggleBtn) => {
      toggleBtn.addEventListener('click', handleSubMenuToggleBtnClick)
    })
    tier1Links.forEach((link) => {
      link.setAttribute('tabindex', '-1')
      if (!link.classList.contains('js-toggle-sub-menu')) {
        link.addEventListener('click', closeMainMenu)
      }
    })
    tier2Links.forEach((link) => {
      link.setAttribute('tabindex', '-1')
      link.addEventListener('click', closeMainMenu)
    })
  }

  /**
   * Tear down event listeners and reset any state, classes/styling/attributes dynamically applied to elements
   */
  const killMobileMenu = () => {
    toggleBtnEl.removeEventListener('click', handleToggleBtnClick)
    toggleSubMenuBtnEls.forEach((toggleBtn) => {
      toggleBtn.removeEventListener('click', handleSubMenuToggleBtnClick)
    })
    headerEl.querySelectorAll('.js-tier-1-link, .js-tier-2-link, .js-link-outside-menu').forEach((focusableItem) => {
      focusableItem.removeAttribute('tabindex')
      focusableItem.removeEventListener('click', closeMainMenu)
    })
    resetOverlay()
    resetMainMenu()
    resetSubMenu()
    resetMenuState()
  }

  /**
   * Handle toggling main menu open/closed when menu toggle button clicked
   */
  const handleToggleBtnClick = (e) => {
    if (menuState.isAnimating) return

    if (menuState.isOpen) {
      closeMainMenu()
    } else {
      openMainMenu()
    }
  }

  /**
   * - Open main menu and show gradient overlay bg between page elements/open menu
   * - Update a11y properties (aria-expanded, tabindex to prevent keyboard nav to hidden elements)
   * - Enable focus trap to loop keyboard navigation within menu element while open
   * - Set required classes/styling
   * - Update menu state
   */
  const openMainMenu = () => {
    body.classList.add('_overflow-hidden--body')
    html.classList.add('_overflow-hidden')
    tier1Links.forEach((link) => {
      link.removeAttribute('tabindex')
    })
    linksOutsideFocusTrap.forEach((link) => {
      link.setAttribute('tabindex', '-1')
    })

    showOverlay()
    enableFocusTrap()

    toggleBtnEl.setAttribute('aria-expanded', 'true')
    headerEl.classList.add('c-header--mob-nav-open')
    openMenu(navEl, () => {
      menuState.isOpen = true
    })
  }

  /**
   * - Close main menu, along with any open sub menus
   * - Update a11y properties (aria-expanded, tabindex to prevent keyboard nav to hidden elements)
   * - Disable focus trap inside expanded menu element
   * - Reset any classes/styling applied
   * - Update menu state
   */
  const closeMainMenu = () => {
    if (menuState.activeSubMenuId.length) {
      closeActiveSubMenu()
    }

    body.classList.remove('_overflow-hidden--body')
    html.classList.remove('_overflow-hidden')

    hideOverlay()
    tier1Links.forEach((link) => {
      link.setAttribute('tabindex', '-1')
    })
    linksOutsideFocusTrap.forEach((link) => {
      link.removeAttribute('tabindex')
    })
    disableFocusTrap()

    toggleBtnEl.setAttribute('aria-expanded', 'false')
    headerEl.classList.remove('c-header--mob-nav-open')
    closeMenu(navEl, () => {
      menuState.isOpen = false
    })
  }

  /**
   * Handle toggling sub menu open/closed when top level item (with sub menu) clicked.
   * Only one sub menu can be open at a time (opening one should close any already open)
   */
  const handleSubMenuToggleBtnClick = (e) => {
    e.preventDefault()
    if (menuState.isAnimating) return

    const subMenuId = `#${e.target.dataset.submenu}`
    const isOpenSubMenu = menuState.activeSubMenuId === subMenuId

    closeActiveSubMenu()

    if (!isOpenSubMenu) {
      const subMenuEl = headerEl.querySelector(subMenuId)
      if (subMenuEl) {
        subMenuEl.querySelectorAll('.js-tier-2-link').forEach((link) => {
          link.removeAttribute('tabindex')
        })
        e.target.classList.add('c-main-nav__link--sub-menu-open')
        e.target.setAttribute('aria-expanded', 'true')
        openMenu(subMenuEl, () => {
          menuState.activeSubMenuId = subMenuId
        })
      }
    }
  }

  /**
   * - If a sub menu is open, close it
   * - Update a11y properties (aria-expanded, tabindex to prevent keyboard nav to hidden elements)
   * - Set required classes/styling
   * - Update menu state
   */
  const closeActiveSubMenu = () => {
    if (!menuState.activeSubMenuId) return

    const activeSubMenuEl = headerEl.querySelector(menuState.activeSubMenuId)
    if (activeSubMenuEl) {
      activeSubMenuEl.querySelectorAll('.js-tier-2-link').forEach((link) => {
        link.setAttribute('tabindex', '-1')
      })
      const toggleBtn = headerEl.querySelector(`[data-submenu="${menuState.activeSubMenuId.substring(1)}"]`)
      if (toggleBtn) {
        toggleBtn.classList.remove('c-main-nav__link--sub-menu-open')
        toggleBtn.setAttribute('aria-expanded', 'false')
      }
      closeMenu(activeSubMenuEl, () => {
        menuState.activeSubMenuId = ''
      })
    }
  }

  /**
   * Listen to keyboard nav events while main navigation open
   */
  const enableFocusTrap = () => {
    headerEl.addEventListener('keydown', handleKeyboardNavigation)
  }

  /**
   * Remove keyboard nav event listener when main menu closed
   */
  const disableFocusTrap = () => {
    headerEl.removeEventListener('keydown', handleKeyboardNavigation)
  }

  /**
   * Restrict keyboard navigation between focusable elements to menu items only
   * when main menu open and rest of page visibly hidden from user. Focus loops back
   * to first item after user tries to tab on from last element.
   */
  const handleKeyboardNavigation = (e) => {
    if (e.key === 'Tab') {
      const firstFocusableEl = headerEl.querySelector('a')
      const lastFocusableEl = tier1Links[tier1Links.length - 1]
      if (e.shiftKey && document.activeElement === firstFocusableEl) {
        e.preventDefault()
        lastFocusableEl.focus()
      } else if (!e.shiftKey && document.activeElement === lastFocusableEl) {
        e.preventDefault()
        firstFocusableEl.focus()
      }
    } else if (e.key === 'Escape') {
      closeMainMenu()
      toggleBtnEl.focus()
    }
  }

  /**
   * Remove any styles/classes dynamically applied to header/menu elements
   */
  const resetMainMenu = () => {
    navEl.removeAttribute('style')
    headerEl.classList.remove('c-header--mob-nav-open')
  }

  /**
   * Remove any styles/classes dynamically applied to sub menu elements
   */
  const resetSubMenu = () => {
    const toggleBtn = headerEl.querySelector(`[data-submenu="${menuState.activeSubMenuId.substring(1)}"]`)
    if (toggleBtn) {
      toggleBtn.classList.remove('c-main-nav__link--sub-menu-open')
      toggleBtn.setAttribute('aria-expanded', 'false')
    }
    subMenuEls.forEach((subMenu) => {
      subMenu.removeAttribute('style')
    })
  }

  const resetMenuState = () => {
    menuState = {
      isOpen: false,
      activeSubMenuId: '',
      isAnimating: false,
    }
  }

  /**
   * Handle expand animation of given menu or sub menu container element.
   */
  const openMenu = (menuEl, onComplete) => {
    menuState.isAnimating = true

    anime({
      targets: menuEl,
      height: (el) => el.scrollHeight,
      opacity: [0, 1],
      easing: menuAnimationOpts.easing,
      duration: menuAnimationOpts.speed,
      complete: (anim) => {
        menuState.isAnimating = false

        // Return element to height auto after animation completed
        const animatedEl = anim.animatables[0].target
        animatedEl.style.height = 'auto'

        if (onComplete) onComplete()
      },
    })

    homeLogoLink.addEventListener('click', closeMainMenu)
  }

  /**
   * Handle contract animation of given menu or sub menu container element.
   */
  const closeMenu = (menuEl, onComplete) => {
    // Set static height before animating between values
    menuEl.style.height = menuEl.scrollHeight + 'px'
    menuState.isAnimating = true

    anime({
      targets: menuEl,
      height: 0,
      opacity: [1, 0],
      easing: menuAnimationOpts.easing,
      duration: menuAnimationOpts.speed,
      complete: () => {
        menuState.isAnimating = false
        if (onComplete) onComplete()
      },
    })

    homeLogoLink.removeEventListener('click', closeMainMenu)
  }

  /**
   * Handle fade in animation of gradient overlay background element
   */
  const showOverlay = () => {
    anime({
      targets: overlayEl,
      opacity: 1,
      easing: overlayAnimationOpts.easing,
      duration: overlayAnimationOpts.speed,
      complete: () => {
        overlayEl.classList.add('c-header__menu-bg-overlay--active')
        overlayEl.addEventListener('click', closeMainMenu)
      },
    })
  }

  /**
   * Handle fade out animation of gradient overlay background element
   */
  const hideOverlay = () => {
    anime({
      targets: overlayEl,
      opacity: 0,
      easing: overlayAnimationOpts.easing,
      duration: overlayAnimationOpts.speed,
      complete: () => {
        resetOverlay()
      },
    })
  }

  /**
   * Remove any events, styles/classes dynamically applied to overlay element
   */
  const resetOverlay = () => {
    overlayEl.classList.remove('c-header__menu-bg-overlay--active')
    overlayEl.removeAttribute('style')
    overlayEl.removeEventListener('click', closeMainMenu)
  }

  return {
    init: () => {
      initMobileMenu()
    },
    kill: () => {
      killMobileMenu()
    },
    closeMenu: () => {
      closeMainMenu()
    },
  }
})()

export default MobileMenu
